import React, { Dispatch, SetStateAction, useState } from 'react';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { Button, ButtonGroup } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { ChartTitleAndDescription, FormInput } from 'src/components/molecules';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import * as Types from 'src/types';
import { CHART_IDS, trackChartViewChanged } from 'src/utils/analytics';
import { DateFormats, formatDate } from 'src/utils/time';
import {
  ChartComponentContainer,
  DownloadButtonContainer,
  GraphButtonContainer,
  GraphPanelContainer,
  IntroComponentContainer,
  StyledTabs,
} from './GraphPanel.styles';

interface GraphPanelProps {
  onDownloadInsights?: (value: DefaultOption, printChart: () => void) => void;
  onFiltersOpen: () => void;
  chartTitle: string;
  chartTitleDescription?: string;
  headers: string[];
  panels: React.ReactNode[];
  dashboard: 'organisational' | 'operational';
  chartId: CHART_IDS;
  buttonGroupOptions?: string[];
  setSelectedButtonGroupOption?: (option: string) => void;
  selectedButtonGroupOption?: string;
  pdfDownloadRef: React.RefObject<HTMLDivElement | null>;
  pdfDownloadWidth?: number;
  pdfDownloadHeight?: number;
  togglePdfDownloading?: Dispatch<SetStateAction<boolean>>;
}

export const GraphPanel = ({
  onDownloadInsights,
  onFiltersOpen,
  chartTitle,
  chartTitleDescription,
  headers,
  panels,
  dashboard,
  chartId,
  selectedButtonGroupOption,
  buttonGroupOptions,
  setSelectedButtonGroupOption,
  pdfDownloadRef,
  pdfDownloadWidth,
  pdfDownloadHeight,
  togglePdfDownloading,
}: GraphPanelProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const print = useReactToPrint({
    content: () => pdfDownloadRef.current,
    documentTitle: `${chartTitle}_${formatDate(
      new Date(),
      DateFormats.DD_MMM_YYYY_DASH,
    )}.pdf`,
    onBeforeGetContent: async () => {
      if (pdfDownloadRef.current) {
        pdfDownloadRef.current.style.width = `${pdfDownloadWidth ?? 1000}px`;
        pdfDownloadRef.current.style.height = `${pdfDownloadHeight ?? 500}px`;
      }
      togglePdfDownloading?.(true);
    },
    onAfterPrint: () => {
      if (pdfDownloadRef.current) {
        pdfDownloadRef.current.style.width = '100%';
        pdfDownloadRef.current.style.height = '100%';
      }
      togglePdfDownloading?.(false);
    },
    pageStyle: `
      @page {size: ${pdfDownloadWidth ?? 1000}px ${
        pdfDownloadHeight ? pdfDownloadHeight + 100 : 600
      }px;}
    `,
  });
  const organisation = useSelector((state: Types.State) => state.organisation);
  const features = useSelector((state: Types.State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const handleTrackChartViewChange = (index: number) => {
    if (organisation.organisation && segmentEnabled) {
      trackChartViewChanged(
        organisation.organisation.id,
        dashboard,
        chartId,
        headers[index],
      );
    }
  };
  return (
    <GraphPanelContainer>
      <IntroComponentContainer>
        <ChartTitleAndDescription
          chartTitle={chartTitle}
          chartTitleDescription={chartTitleDescription}
        />
        <DownloadButtonContainer>
          <FormInput
            variant={Types.FormInputVariant.DROPDOWN}
            data-testid={`download-insights-${chartTitle}`}
            buttonText="Download insights"
            dropdownButtonSize="small"
            hideDropdownIndicator={true}
            options={[
              { label: 'CSV', value: 'csv' },
              { label: 'XLS', value: 'xls' },
              { label: 'PDF', value: 'pdf' },
            ]}
            onChange={(value) => {
              if (onDownloadInsights) {
                onDownloadInsights(value as DefaultOption, print);
              } else {
                print();
              }
            }}
            controlShouldRenderValue={false}
          />
        </DownloadButtonContainer>
      </IntroComponentContainer>
      <ChartComponentContainer>
        <StyledTabs
          activeTab={activeTab}
          onClick={setActiveTab}
          headers={headers}
          panels={panels}
          handleTracking={handleTrackChartViewChange}
        />

        <GraphButtonContainer>
          {setSelectedButtonGroupOption &&
            selectedButtonGroupOption &&
            buttonGroupOptions && (
              <ButtonGroup
                options={buttonGroupOptions}
                setSelectedOption={setSelectedButtonGroupOption}
                size="small"
                selectedOption={selectedButtonGroupOption}
              />
            )}
          <Button size={'small'} variant={'secondary'} onClick={onFiltersOpen}>
            <StyledIconInButton
              size="small"
              position="left"
              className="uil uil-sliders-v-alt"
            />
            {'Chart filters'}
          </Button>
        </GraphButtonContainer>
      </ChartComponentContainer>
    </GraphPanelContainer>
  );
};
